'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Save, X } from 'lucide-react'
import type { ItemResult } from '@creanizer/types'

interface ItemEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: Partial<ItemResult>) => void
  itemResult: ItemResult
  title?: string
}

export function ItemEditModal({
  isOpen,
  onClose,
  onSave,
  itemResult,
  title = '編集'
}: ItemEditModalProps) {
  const [editedName, setEditedName] = useState(itemResult.basicInfo.name)
  const [editedType, setEditedType] = useState(itemResult.basicInfo.type)
  const [editedCategory, setEditedCategory] = useState(itemResult.basicInfo.category)
  const [editedRarity, setEditedRarity] = useState(itemResult.basicInfo.rarity)
  const [editedSummary, setEditedSummary] = useState(itemResult.basicInfo.summary)
  const [editedDescription, setEditedDescription] = useState(itemResult.basicInfo.description)
  const [editedMaterial, setEditedMaterial] = useState(itemResult.appearance.material)
  const [editedSize, setEditedSize] = useState(itemResult.appearance.size)
  const [editedWeight, setEditedWeight] = useState(itemResult.appearance.weight)
  const [editedVisualDescription, setEditedVisualDescription] = useState(itemResult.appearance.visualDescription)
  const [editedPrimaryEffect, setEditedPrimaryEffect] = useState(itemResult.effects.primaryEffect.description)

  // itemResultが変更されたら編集中の値をリセット
  useEffect(() => {
    setEditedName(itemResult.basicInfo.name)
    setEditedType(itemResult.basicInfo.type)
    setEditedCategory(itemResult.basicInfo.category)
    setEditedRarity(itemResult.basicInfo.rarity)
    setEditedSummary(itemResult.basicInfo.summary)
    setEditedDescription(itemResult.basicInfo.description)
    setEditedMaterial(itemResult.appearance.material)
    setEditedSize(itemResult.appearance.size)
    setEditedWeight(itemResult.appearance.weight)
    setEditedVisualDescription(itemResult.appearance.visualDescription)
    setEditedPrimaryEffect(itemResult.effects.primaryEffect.description)
  }, [itemResult])

  const handleSave = () => {
    onSave({
      basicInfo: {
        ...itemResult.basicInfo,
        name: editedName,
        type: editedType,
        category: editedCategory,
        rarity: editedRarity,
        summary: editedSummary,
        description: editedDescription
      },
      appearance: {
        ...itemResult.appearance,
        material: editedMaterial,
        size: editedSize,
        weight: editedWeight,
        visualDescription: editedVisualDescription
      },
      effects: {
        ...itemResult.effects,
        primaryEffect: {
          ...itemResult.effects.primaryEffect,
          description: editedPrimaryEffect
        }
      }
    })
    onClose()
  }

  const handleCancel = () => {
    // 編集内容をリセット
    setEditedName(itemResult.basicInfo.name)
    setEditedType(itemResult.basicInfo.type)
    setEditedCategory(itemResult.basicInfo.category)
    setEditedRarity(itemResult.basicInfo.rarity)
    setEditedSummary(itemResult.basicInfo.summary)
    setEditedDescription(itemResult.basicInfo.description)
    setEditedMaterial(itemResult.appearance.material)
    setEditedSize(itemResult.appearance.size)
    setEditedWeight(itemResult.appearance.weight)
    setEditedVisualDescription(itemResult.appearance.visualDescription)
    setEditedPrimaryEffect(itemResult.effects.primaryEffect.description)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">名前</Label>
              <Input
                id="name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="アイテム名"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="rarity">レアリティ</Label>
              <Select value={editedRarity} onValueChange={setEditedRarity}>
                <SelectTrigger id="rarity" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="common">コモン</SelectItem>
                  <SelectItem value="uncommon">アンコモン</SelectItem>
                  <SelectItem value="rare">レア</SelectItem>
                  <SelectItem value="epic">エピック</SelectItem>
                  <SelectItem value="legendary">レジェンダリー</SelectItem>
                  <SelectItem value="mythic">ミシック</SelectItem>
                  <SelectItem value="unique">ユニーク</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="type">タイプ</Label>
              <Input
                id="type"
                value={editedType}
                onChange={(e) => setEditedType(e.target.value)}
                placeholder="アイテムタイプ"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="category">カテゴリー</Label>
              <Input
                id="category"
                value={editedCategory}
                onChange={(e) => setEditedCategory(e.target.value)}
                placeholder="カテゴリー"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="summary">概要</Label>
            <Textarea
              id="summary"
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              placeholder="アイテムの概要"
              rows={2}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="description">詳細説明</Label>
            <Textarea
              id="description"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="詳細な説明"
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="visualDescription">外見の説明</Label>
            <Textarea
              id="visualDescription"
              value={editedVisualDescription}
              onChange={(e) => setEditedVisualDescription(e.target.value)}
              placeholder="外見の詳細な説明"
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="material">素材</Label>
              <Input
                id="material"
                value={editedMaterial}
                onChange={(e) => setEditedMaterial(e.target.value)}
                placeholder="素材"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="size">サイズ</Label>
              <Input
                id="size"
                value={editedSize}
                onChange={(e) => setEditedSize(e.target.value)}
                placeholder="サイズ"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="weight">重量</Label>
              <Input
                id="weight"
                value={editedWeight}
                onChange={(e) => setEditedWeight(e.target.value)}
                placeholder="重量"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="primaryEffect">主要効果</Label>
            <Textarea
              id="primaryEffect"
              value={editedPrimaryEffect}
              onChange={(e) => setEditedPrimaryEffect(e.target.value)}
              placeholder="主要な効果の説明"
              rows={3}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}